import React from "react";
import { Star, Award, Heart } from "lucide-react";

/**
 * PostTypeSelector - UI for selecting post types (glow, proud, grateful)
 * Shows different options based on whether it's Glow Game or a contest
 */
const PostTypeSelector = ({
	selectedPostType,
	onPostTypeSelect,
	mediaFile,
	mediaPreview,
	onShowCaptureSelector,
	isGlowGame,
	userLumens = 0,
}) => {
	const gratefulCost = 2;
	const hasEnoughLumens = userLumens >= gratefulCost;

	const handlePostTypeClick = (type) => {
		if (selectedPostType === type && !mediaFile && !mediaPreview) {
			// If clicking the same type and no media, show capture selector
			onShowCaptureSelector();
		} else {
			onPostTypeSelect(type);
		}
	};

	if (isGlowGame) {
		// Show all three post types with descriptions for Glow Game
		return (
			<div className="space-y-3">
				{/* Glow Button */}
				<button
					onClick={() => handlePostTypeClick("glow")}
					className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600 ${
						selectedPostType === "glow" ? "ring-4 ring-yellow-300" : ""
					}`}
				>
					<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
						<Star size={24} className="text-yellow-500" />
					</div>
					<div className="flex-1 text-left">
						<h3 className="text-lg font-bold text-white">Create Glow Post</h3>
						<p className="text-sm text-yellow-100">
							Share a kind act you did for others
						</p>
					</div>
					<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
						<div className="relative h-4 w-4">
							<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
							<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
						</div>
						<span className="text-sm font-bold text-white">1-15</span>
					</div>
				</button>

				{/* Proud Button */}
				<button
					onClick={() => onPostTypeSelect("pride")}
					className={`flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 p-4 shadow-lg transition-all hover:scale-105 hover:from-purple-600 hover:to-purple-700 ${
						selectedPostType === "pride" ? "ring-4 ring-purple-300" : ""
					}`}
				>
					<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
						<Award size={24} className="text-purple-500" />
					</div>
					<div className="flex-1 text-left">
						<h3 className="text-lg font-bold text-white">Create Proud Post</h3>
						<p className="text-sm text-purple-100">
							Celebrate your achievements
						</p>
					</div>
				</button>

				{/* Grateful Button */}
				<button
					onClick={() => hasEnoughLumens && handlePostTypeClick("grateful")}
					disabled={!hasEnoughLumens}
					className={`flex w-full transform items-center gap-4 rounded-xl p-4 shadow-lg transition-all ${
						hasEnoughLumens
							? `bg-gradient-to-r from-green-500 to-green-600 hover:scale-105 hover:from-green-600 hover:to-green-700 ${
									selectedPostType === "grateful" ? "ring-4 ring-green-300" : ""
								}`
							: "cursor-not-allowed bg-gradient-to-r from-gray-400 to-gray-500 opacity-70"
					}`}
				>
					<div
						className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white ${!hasEnoughLumens ? "opacity-50" : ""}`}
					>
						<Heart
							size={24}
							className={hasEnoughLumens ? "text-green-500" : "text-gray-400"}
						/>
					</div>
					<div className="flex-1 text-left">
						<h3 className="text-lg font-bold text-white">
							Create Grateful Post
						</h3>
						{hasEnoughLumens ? (
							<p className="text-sm text-green-100">
								Express gratitude to someone special
							</p>
						) : (
							<p className="text-sm font-semibold text-red-200">
								Insufficient Lumens
							</p>
						)}
					</div>
					<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-red-500/30 px-3 py-2 backdrop-blur-sm">
						<div className="relative h-4 w-4">
							<div
								className={`absolute inset-0 rounded-full bg-red-200 blur-sm ${hasEnoughLumens ? "animate-pulse" : ""}`}
							/>
							<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-red-100 to-red-200" />
						</div>
						<span className="text-sm font-bold text-white">-2</span>
					</div>
				</button>
			</div>
		);
	}
	// Show Submit for Contest button for other contests
	return (
		<button
			onClick={() => {
				if (!mediaFile && !mediaPreview) {
					onShowCaptureSelector();
				}
				onPostTypeSelect("glow");
			}}
			className="flex w-full transform items-center gap-4 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 shadow-lg transition-all hover:scale-105 hover:from-yellow-500 hover:to-yellow-600"
		>
			<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white">
				<Star size={24} className="text-yellow-500" />
			</div>
			<div className="flex-1 text-left">
				<h3 className="text-lg font-bold text-white">Submit for Contest</h3>
				<p className="text-sm text-yellow-100">
					Your submission will also be graded for glow
				</p>
			</div>
			<div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white/20 px-3 py-2 backdrop-blur-sm">
				<div className="relative h-4 w-4">
					<div className="absolute inset-0 animate-pulse rounded-full bg-yellow-200 blur-sm" />
					<div className="relative h-4 w-4 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200" />
				</div>
				<span className="text-sm font-bold text-white">1-15</span>
			</div>
		</button>
	);
};

export default PostTypeSelector;
